import { useState } from 'react'

import { saveAs } from 'file-saver'

import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react'
import { createColumnHelper } from '@tanstack/react-table'
import ReactSelect from 'react-select'

import EditableTable from '@/components/Table/EditableTable'
import { createCsvFromAoA } from '@/utils/fileHelper'

import { DOCUMENT_TEMPLATE_TYPE_OPTIONS } from '../../documentTemplate.constant'
import useDocumentTemplate from '../../useDocumentTemplate'

const CSV_HEADER = ['Variable Name', 'Variable Label', 'Variable Source', 'Example Value']

const columnHelper = createColumnHelper()

const columns = [
  columnHelper.accessor('key', {
    header: () => <span>Variable Name</span>,
    size: 260,
    cell: ({ row: { original } }) => (
      <Box textAlign="left">
        <Text fontWeight="600">{original.key}</Text>
        <Text fontSize="xs" color="gray.500">
          {original.label}
        </Text>
      </Box>
    ),
  }),
  columnHelper.accessor('source', {
    header: () => <span>Variable Source</span>,
    size: 260,
  }),
  columnHelper.accessor('sampleValue', {
    header: () => <span>Example Value</span>,
    size: 300,
    cell: ({ getValue }) => (
      <Text textAlign="left" whiteSpace="pre-wrap">
        {getValue()}
      </Text>
    ),
  }),
]

/**
 * A read-only reference for what `{{key}}` placeholders an admin can type into
 * a template of a given type. The catalogue is a small, already-cached array
 * (not a server-paginated list), so this is a plain EditableTable rather than
 * the FullTable machinery built for list endpoints.
 */
const VariableLibraryModal = ({ isOpen, onClose }) => {
  const [type, setType] = useState('')

  const { useFetchVariables } = useDocumentTemplate()
  const { data: variables = [], isLoading } = useFetchVariables(type)

  const handleClose = () => {
    setType('')
    onClose()
  }

  const handleExportCsv = () => {
    const rows = variables.map(({ key, label, source, sampleValue }) => [key, label, source, sampleValue])
    const csvBlob = createCsvFromAoA([CSV_HEADER, ...rows])
    if (!csvBlob) return

    const typeLabel = DOCUMENT_TEMPLATE_TYPE_OPTIONS.find((option) => option.value === type)?.label ?? type
    saveAs(csvBlob, `Variable Library - ${typeLabel}.csv`)
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="2xl">
      <ModalOverlay />
      <ModalContent maxW="60%">
        <ModalHeader>Manage Library</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Flex mb={4} gap={3} alignItems="center">
            {/* A native <select> popup renders misaligned and clipped under fractional OS display scaling */}
            <Box flex="1">
              <ReactSelect
                aria-label="Template type"
                placeholder="Select type"
                options={DOCUMENT_TEMPLATE_TYPE_OPTIONS}
                value={DOCUMENT_TEMPLATE_TYPE_OPTIONS.find((option) => option.value === type) ?? null}
                onChange={(option) => setType(option?.value ?? '')}
                menuPosition="fixed"
              />
            </Box>
            <Button onClick={handleExportCsv} isDisabled={!type || variables.length === 0} flexShrink={0}>
              Export to CSV
            </Button>
          </Flex>

          {!type && (
            <Text fontSize="sm" color="gray.500">
              Select a type to see its variable library.
            </Text>
          )}

          {type && !isLoading && variables.length === 0 && (
            <Text fontSize="sm" color="gray.500">
              No variables are defined for this type.
            </Text>
          )}

          {type && variables.length > 0 && (
            <Box overflowX="auto">
              <EditableTable columnDef={columns} data={variables} tableWidth="100%" />
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default VariableLibraryModal
